import { Checkbox } from '@roger/ui-components';
import { useBespokeSectionContext } from './BespokeSectionContext';
import { useQuestionFieldDisabled } from './useQuestionFieldDisabled';
import { QidTag } from '../../../dev/QidTag';
import {
  joinClasses,
  BESPOKE_INLINE_CONTROL_LABEL_CLASS,
  BESPOKE_FIELD_STACK_GAP_CLASS,
  BESPOKE_CHECKBOX_CLASS,
  BESPOKE_CONTROL_ROW_CLASS,
  BESPOKE_OPTION_LABEL_DISABLED_CLASS,
  BESPOKE_EMPTY_STATE_CLASS,
  BESPOKE_EMPTY_STATE_CODE_CLASS,
} from './fieldClassSlots';
import { resolveControlClassName } from './resolveFieldClassNames';
import { resolveYesNoOptionPair } from './yesNoRadio';
import { CmsFieldError } from '../../CmsFieldError';
import { useCmsQuestionError } from '../../cmsFieldErrors';

export interface OASISYesNoCheckboxProps {
  qid: string;
  /** Display-only replacement for the schema prompt; answer binding still uses qid. */
  labelOverride?: string;
  className?: string;
  style?: React.CSSProperties;
  innerClassName?: string;
  labelClassName?: string;
  hideLabel?: boolean;
  optionRowClassName?: string;
  controlClassName?: string;
  /** @deprecated Prefer `controlClassName`. */
  checkboxClassName?: string;
}

/**
 * Single checkbox bound to a **radio** question whose schema `values[]` are
 * exactly `"Yes"` and `"No"` (case-insensitive). Checked ⇒ schema yes value,
 * unchecked ⇒ schema no value (including empty / unset answers).
 */
export function OASISYesNoCheckbox({
  qid,
  labelOverride,
  className,
  style,
  innerClassName,
  labelClassName,
  hideLabel,
  optionRowClassName,
  controlClassName,
  checkboxClassName,
}: OASISYesNoCheckboxProps) {
  const { answers, onAnswerChange, schemaMap } = useBespokeSectionContext();
  const cmsError = useCmsQuestionError(qid);
  const question = schemaMap.get(qid);
  const inheritedDisabled = useQuestionFieldDisabled(question);

  const pair = resolveYesNoOptionPair(question?.values);
  const raw = answers[qid] as string | undefined;
  const value = typeof raw === 'string' ? raw.trim() : '';
  const id = `${qid}-yes-no-checkbox`;

  const label =
    labelOverride ?? question?.question ?? question?.description ?? qid;

  const blockLabelCls = joinClasses(
    BESPOKE_INLINE_CONTROL_LABEL_CLASS,
    'cursor-pointer select-none',
    inheritedDisabled && BESPOKE_OPTION_LABEL_DISABLED_CLASS,
    labelClassName
  );

  const innerDefault = joinClasses(
    'flex min-w-0 flex-col',
    BESPOKE_FIELD_STACK_GAP_CLASS
  );
  const inner = innerClassName != null ? innerClassName : innerDefault;
  const resolvedCheckboxClassName = joinClasses(
    BESPOKE_CHECKBOX_CLASS,
    'shrink-0',
    resolveControlClassName(controlClassName, checkboxClassName)
  );
  const resolvedOptionRowClassName = joinClasses(
    BESPOKE_CONTROL_ROW_CLASS,
    optionRowClassName
  );

  if (!pair) {
    return (
      <div
        data-qid={qid}
        className={joinClasses('min-w-0', className)}
        style={style}
      >
        <QidTag qid={qid} />
        <p className={BESPOKE_EMPTY_STATE_CLASS}>
          <code className={BESPOKE_EMPTY_STATE_CODE_CLASS}>
            OASISYesNoCheckbox
          </code>{' '}
          needs a radio question with exactly two options{' '}
          <code className={BESPOKE_EMPTY_STATE_CODE_CLASS}>Yes</code> and{' '}
          <code className={BESPOKE_EMPTY_STATE_CODE_CLASS}>No</code>{' '}
          (case-insensitive) for{' '}
          <code className={BESPOKE_EMPTY_STATE_CODE_CLASS}>{qid}</code>.
        </p>
      </div>
    );
  }

  const checked = value === pair.yesValue;
  const onCheckedChange = (state: boolean | 'indeterminate') => {
    onAnswerChange(qid, state === true ? pair.yesValue : pair.noValue);
  };

  return (
    <div
      data-qid={qid}
      className={joinClasses('min-w-0', className)}
      style={style}
    >
      <QidTag qid={qid} />
      <div className={inner}>
        <div className={resolvedOptionRowClassName}>
          <Checkbox
            id={id}
            checked={checked}
            disabled={inheritedDisabled}
            onCheckedChange={onCheckedChange}
            aria-label={hideLabel ? label : undefined}
            className={resolvedCheckboxClassName}
          />
          {!hideLabel && (
            <label
              htmlFor={id}
              className={joinClasses('min-w-0', blockLabelCls)}
            >
              {label}
            </label>
          )}
        </div>
      </div>
      <CmsFieldError fieldId={`oasis-q-${qid}`} message={cmsError} />
    </div>
  );
}

/** Vibecode authoring notes for `OASISYesNoCheckbox` nodes (empty = omit from LLM bundle). */
export const OASIS_YES_NO_CHECKBOX_VIBECODE =
  '`OASISYesNoCheckbox`: only when `yes_no_checkbox_eligible` (Yes/No radio). Not for coded 0/1 radios.';
